import { Provider as StoreProvider } from 'react-redux';
import CartInspector from 'components/_global/cart-inspector';
import NavigationBar from 'components/_global/navigation-bar';
import Layout from 'components/_global/layout';
import OnlineStatusListener from 'components/_global/online-status-listener';
import store from '../redux/store';
import anotherdayonlyTheme from 'themes/anotherdayonly';
import onedayonlyTheme from 'themes/onedayonly';
import onenightonlyTheme from 'themes/onenightonly';
import clearancesaleTheme from 'themes/clearancesale';
import everydayessentialsTheme from 'themes/everydayessentials';
import extratimedealsTheme from 'themes/extratimedeals';
import giftvouchersTheme from 'themes/giftvouchers';
import { AppProps } from 'next/app';
import { Global, ThemeProvider } from '@emotion/react';
import globalStyles from 'components/_global/styles';
import 'what-input';
import Script from 'next/script';
import {
  StickyMessageInterface,
  PageLayoutProps,
  PageProps,
} from 'types/types';
import ErrorBoundary from 'components/_global/error-boundary';
import BroadcastChannelReceiver from 'components/_global/broadcast-channel/receiver';
import { canonicalUrl } from '../util/url';
import SmartechTokenLoader from 'components/netcore';
import SessionExpiryListener from 'components/_global/session-expiry-listener';
import AppProviders from 'components/_global/app/providers';

const themes = {
  anotherdayonly: anotherdayonlyTheme,
  clearancesale: clearancesaleTheme,
  onedayonly: onedayonlyTheme,
  onenightonly: onenightonlyTheme,
  everydayessentials: everydayessentialsTheme,
  giftvouchers: giftvouchersTheme,
  extratimedeals: extratimedealsTheme,
};

type AppInnerProps = Pick<AppProps, 'Component'> &
  Pick<AppProps, 'pageProps'> & {
    themeName?: string;
    messages?: StickyMessageInterface[];
    isAnotherDayOnlyActive?: boolean;
    layoutProps: PageLayoutProps;
  };

const AppInner = ({
  Component: PageComponent,
  pageProps,
  themeName,
  messages,
  isAnotherDayOnlyActive,
  layoutProps,
}: AppInnerProps) => {
  let theme = themeName;
  if (!theme) {
    if (isAnotherDayOnlyActive) {
      theme = 'anotherdayonly';
    } else {
      theme = 'onedayonly';
    }
  }

  return (
    <ThemeProvider theme={themes[theme]}>
      <Global styles={globalStyles} />
      <OnlineStatusListener />

      <Layout messages={messages} {...layoutProps}>
        <PageComponent {...pageProps} />
      </Layout>

      <CartInspector />
      <BroadcastChannelReceiver />
      <SessionExpiryListener />

      {/* NOTE: will internally check if it's meant to be active before rendering */}
      <NavigationBar />
    </ThemeProvider>
  );
};

const App = ({
  router: { asPath, isReady },
  ...restProps
}: AppProps<PageProps>) => {
  const messages = restProps?.pageProps?.meta?.messages || undefined;
  const theme = restProps?.pageProps?.meta?.theme || undefined;
  const isAnotherDayOnlyActive =
    restProps?.pageProps?.meta?.isAnotherDayOnlyActive || undefined;

  const layoutProps: PageLayoutProps = {
    showAdoHeader: !!isAnotherDayOnlyActive,
    ...(restProps?.pageProps?.layout || {}),
  };

  // check if we already have a canonical tag defined,
  // else try and generate one (but opt to do nothing rather if there are ANY issues)
  if (!layoutProps.metaTags?.canonical && isReady && asPath) {
    const canonical = canonicalUrl(asPath);
    if (canonical) {
      layoutProps.metaTags = { ...layoutProps.metaTags, canonical };
    }
  }

  return (
    <AppProviders>
      <Script
        id="browser-check"
        src="/browser-check.js"
        strategy="lazyOnload"
      />

      <ErrorBoundary>
        <SmartechTokenLoader />
      </ErrorBoundary>

      <StoreProvider store={store}>
        <AppInner
          {...restProps}
          themeName={theme}
          messages={messages}
          isAnotherDayOnlyActive={isAnotherDayOnlyActive}
          layoutProps={layoutProps}
        />
      </StoreProvider>
    </AppProviders>
  );
};

export default App;
